import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import type { Trace } from '@/features/traceability/type/Tool';
import { useMemo } from 'react';
import type { ChartConfig } from '../../type/dynamicChart';
import { COLORS } from '../../utils/palette';
import { getMetricFormatter } from '@/utils/format';
import {
  aggregateTracesByTool,
  aggregateTracesByCategory,
  aggregateTracesByReference,
} from '../../utils/trace';
import { getMetricValue, isCategoricalXAxis } from '../../utils/helper';
import { shortToolId } from '../../utils/format';
import { getSafeAggregationLevel } from '../../utils/chartSafety';
import { computeYFromTraces } from '../../utils/aggregationUtils';
import { SmartLegend } from './SmartLegend';
import type { LegendItem } from './SmartLegend';
import { ChartEmptyState } from './ChartEmptyState';
import { useTranslation } from '@/hooks/useTranslation';
import { getMetricLabelKey } from '../../utils/chartDefaults';

type TLRadarChartProps = {
  config: ChartConfig;
  traces: Trace[];
};

// Check if X-axis is a categorical dimension
// All available metrics for radar chart
const RADAR_METRICS = [
  'totalMachiningDuration',
  'pieceMachiningDuration',
  'machinedPieces',
  'machinedVolume',
  'machinedDistance',
  'malround',
] as const;

type RadarMetric = (typeof RADAR_METRICS)[number];

export function TLRadarChart({ config, traces }: TLRadarChartProps) {
  const { t } = useTranslation();

  // Check if X-axis is categorical dimension
  const isCategorical = isCategoricalXAxis(config.xAxis.metric);

  // Determine aggregation level
  const aggregationLevel = getSafeAggregationLevel(
    isCategorical ? 'category' : config.aggregatedBy,
    traces,
    isCategorical
  );

  // Aggregate data based on X-axis type
  const aggregatedData = useMemo(() => {
    if (isCategorical) {
      // Aggregate traces by categorical dimension
      return aggregateTracesByCategory(
        traces,
        config.xAxis.metric as 'machine' | 'material' | 'toolHolder' | 'lubrication'
      );
    }

    if (aggregationLevel === 'reference') {
      return aggregateTracesByReference(traces);
    }

    if (aggregationLevel === 'trace') {
      return traces;
    }
    // Aggregate by tool
    return aggregateTracesByTool(traces);
  }, [traces, aggregationLevel, isCategorical, config.xAxis.metric]);

  // For radar charts, we have two modes:
  // 1. Multi-category mode: Show all metrics for selected categories (entities)
  // 2. Single-category mode: Compare categories on a single metric

  // Determine if we should show multiple metrics or multiple categories
  const radarMode = config.options.radarMode || 'categories';
  const radarMaxItems = config.options.radarMaxItems || (radarMode === 'metrics' ? 5 : 8);
  const showMultipleMetrics = radarMode === 'metrics';

  // Transform data for radar chart
  const radarData = useMemo(() => {
    if (showMultipleMetrics) {
      // Mode 1: Show multiple metrics for each entity (tool/category)
      // Each metric becomes a point on the radar
      // Each entity becomes a separate radar line

      // Get top N entities by the Y-axis metric
      const sortedEntities = [...aggregatedData]
        .sort((a, b) => {
          const aVal = 'rawTraces' in a
            ? computeYFromTraces(a.rawTraces, config.yAxis.metric, config.yAxis.aggregation)
            : (getMetricValue(a, config.yAxis.metric) || 0);
          const bVal = 'rawTraces' in b
            ? computeYFromTraces(b.rawTraces, config.yAxis.metric, config.yAxis.aggregation)
            : (getMetricValue(b, config.yAxis.metric) || 0);
          return bVal - aVal;
        })
        .slice(0, radarMaxItems);

      // Normalize each metric to 0-100 scale
      const metricRanges = new Map<RadarMetric, { min: number; max: number }>();

      RADAR_METRICS.forEach((metric) => {
        const values = aggregatedData
          .map((item) =>
            'rawTraces' in item
              ? computeYFromTraces(item.rawTraces, metric, undefined)
              : getMetricValue(item, metric)
          )
          .filter((v): v is number => v !== null && v !== 0);

        if (values.length > 0) {
          metricRanges.set(metric, {
            min: Math.min(...values),
            max: Math.max(...values),
          });
        }
      });

      // Create radar data structure
      const data = RADAR_METRICS.map((metric) => {
        const point: Record<string, string | number> = {
          metric: metric,
          displayName: getMetricDisplayName(metric, t),
        };

        sortedEntities.forEach((entity) => {
          // For ToolAggregation/CategoryAggregation: use computeYFromTraces
          // For the Y-axis metric: apply user-selected aggregation; for other metrics: use defaultAggregation
          const isYMetric = metric === config.yAxis.metric;
          const aggKey = isYMetric ? config.yAxis.aggregation : undefined;
          const value = 'rawTraces' in entity
            ? computeYFromTraces(entity.rawTraces, metric, aggKey)
            : getMetricValue(entity, metric);
          const range = metricRanges.get(metric);

          let entityName: string;
          if ('category' in entity) {
            entityName = entity.category;
          } else if ('reference' in entity && 'toolCount' in entity) {
            entityName = entity.reference;
          } else {
            entityName = shortToolId(entity.toolId);
          }

          if (value !== null && range && range.max > range.min) {
            // Normalize to 0-100
            const normalized = ((value - range.min) / (range.max - range.min)) * 100;
            point[entityName] = normalized;
            point[`${entityName}_raw`] = value; // Store raw value for tooltip
          }
        });

        return point;
      });

      return {
        data,
        entities: sortedEntities.map((entity) => {
          if ('category' in entity) return entity.category;
          if ('reference' in entity && 'toolCount' in entity) return entity.reference;
          return shortToolId(entity.toolId);
        }),
        mode: 'metrics' as const,
        metricRanges,
      };
    } else {
      // Mode 2: Compare categories on selected metrics
      // Each category becomes a point on the radar
      // Selected metric determines the values

      const data = aggregatedData.map((entity) => {
        // For ToolAggregation/CategoryAggregation: use computeYFromTraces with rawTraces
        // For ReferenceAggregation: keep getMetricValue (not in F2 scope)
        const value = 'rawTraces' in entity
          ? computeYFromTraces(entity.rawTraces, config.yAxis.metric, config.yAxis.aggregation)
          : (getMetricValue(entity, config.yAxis.metric) || 0);

        let entityName: string;
        if ('category' in entity) {
          entityName = entity.category;
        } else if ('reference' in entity && 'toolCount' in entity) {
          entityName = entity.reference;
        } else {
          entityName = 'toolId' in entity ? shortToolId(entity.toolId) : '';
        }

        return {
          category: entityName,
          value: value || 0,
          traceCount: 'traceCount' in entity ? entity.traceCount : 1,
          toolCount: 'toolCount' in entity ? entity.toolCount : undefined,
        };
      });

      // Sort and limit
      const sortedData = data.sort((a, b) => b.value - a.value).slice(0, radarMaxItems);

      return {
        data: sortedData,
        entities: [],
        mode: 'categories' as const,
        metricRanges: new Map(),
      };
    }
  }, [aggregatedData, config.yAxis.metric, config.yAxis.aggregation, showMultipleMetrics, radarMaxItems, t]);

  const valueFormatter = getMetricFormatter(config.yAxis.metric);

  // Prepare legend items for SmartLegend
  const legendItems: LegendItem[] = useMemo(() => {
    if (radarData.mode === 'metrics') {
      // Multi-metric mode: show entities
      return radarData.entities.map((entity, index) => ({
        value: entity,
        label: entity,
        color: COLORS[index % COLORS.length],
      }));
    } else {
      // Single-metric mode: single data series
      return [
        {
          value: t(getMetricLabelKey(config.yAxis.metric)),
          label: t(getMetricLabelKey(config.yAxis.metric)),
          color: COLORS[0],
        },
      ];
    }
  }, [radarData, config.yAxis.metric, t]);

  if (radarData.data.length === 0) {
    return <ChartEmptyState variant="no-data" />;
  }

  // Render based on mode
  if (radarData.mode === 'metrics') {
    // Multi-metric mode: comparing entities across all metrics
    return (
      <div className="space-y-2">
        <ResponsiveContainer width="100%" height={400}>
          <RadarChart data={radarData.data} margin={{ top: 8, right: 16, bottom: 32, left: 52 }}>
            <PolarGrid />
            <PolarAngleAxis dataKey="displayName" />
            <PolarRadiusAxis angle={90} domain={[0, 100]} />
            <Tooltip
              content={({ payload }) => {
                if (!payload || payload.length === 0) return null;
                const data = payload[0].payload;

                return (
                  <div className="bg-popover border border-border shadow-md rounded-lg p-3 text-xs">
                    <p className="font-medium mb-1">{data.displayName}</p>
                    {payload.map((entry, index) => {
                      const rawValue = data[`${entry.dataKey}_raw`];
                      if (rawValue === undefined) return null;

                      return (
                        <p key={index} className="font-mono" style={{ color: entry.color }}>
                          {entry.name}: {valueFormatter(rawValue)} ({Number(entry.value).toFixed(0)}
                          %)
                        </p>
                      );
                    })}
                  </div>
                );
              }}
            />
            {radarData.entities.map((entity, index) => (
              <Radar
                key={entity}
                name={entity}
                dataKey={entity}
                stroke={COLORS[index % COLORS.length]}
                fill={COLORS[index % COLORS.length]}
                fillOpacity={0.3}
              />
            ))}
          </RadarChart>
        </ResponsiveContainer>

        {/* SmartLegend */}
        <SmartLegend payload={legendItems} layout="horizontal" maxVisibleItems={10} />
      </div>
    );
  } else {
    // Single-metric mode: comparing categories on one metric
    return (
      <div className="space-y-2">
        <ResponsiveContainer width="100%" height={400}>
          <RadarChart data={radarData.data} margin={{ top: 8, right: 16, bottom: 32, left: 52 }}>
            <PolarGrid />
            <PolarAngleAxis dataKey="category" />
            <PolarRadiusAxis angle={90} />
            <Tooltip
              content={({ payload }) => {
                if (!payload || payload.length === 0) return null;
                const data = payload[0].payload;

                return (
                  <div className="bg-popover border border-border shadow-md rounded-lg p-3 text-xs">
                    <p className="font-medium">
                      {isCategorical
                        ? t(getMetricLabelKey(config.xAxis.metric))
                        : aggregationLevel === 'reference'
                          ? t('analytics.dashboard.complexCharts.tooltip.reference')
                          : t('analytics.dashboard.complexCharts.tooltip.category')}
                      : {data.category}
                    </p>
                    <p className="font-mono">
                      {t(getMetricLabelKey(config.yAxis.metric))}: {valueFormatter(data.value)}
                    </p>
                    {typeof data.toolCount === 'number' && (
                      <p className="font-mono">
                        {t('analytics.dashboard.complexCharts.tooltip.toolCount', {
                          count: data.toolCount,
                        })}
                      </p>
                    )}
                    {data.traceCount > 1 && (
                      <p className="font-mono text-muted-foreground">
                        {t('analytics.dashboard.complexCharts.tooltip.aggregatedFrom', {
                          count: data.traceCount,
                        })}
                      </p>
                    )}
                  </div>
                );
              }}
            />
            <Radar
              name={t(getMetricLabelKey(config.yAxis.metric))}
              dataKey="value"
              stroke={COLORS[0]}
              fill={COLORS[0]}
              fillOpacity={0.5}
            />
          </RadarChart>
        </ResponsiveContainer>

        {/* SmartLegend */}
        <SmartLegend payload={legendItems} layout="horizontal" maxVisibleItems={10} />
      </div>
    );
  }
}

function getMetricDisplayName(metric: RadarMetric, t: (key: string) => string): string {
  return t(`analytics.dashboard.complexCharts.configuration.metrics.${metric}`);
}
